import React from "react";
import { Drawer, useMediaQuery } from "@mui/material";
import theme from "../theme";
import {
  leftDrawerWidth,
  RESPONSIVE_DRAWER,
  rightDrawerWidth,
} from "./Common.style";

export type CustomDrawerProps = {
  open: boolean;
  children: React.ReactNode;
  fullWidth?: boolean;
  testId?: string;
  anchor?: "left" | "top" | "right" | "bottom";
  width?: string;
};

export const useRightDrawerWidth = (): string => {
  const isWide = useMediaQuery(`(min-width: ${RESPONSIVE_DRAWER.WIDE_BP}px)`);

  if (isWide) return rightDrawerWidth;
  return `${RESPONSIVE_DRAWER.NARROW_PX}px`;
};

const LEFT_DRAWER_MAX_NARROW_PX = 640;

export const useLeftDrawerWidth = (): string => {
  const isWide = useMediaQuery(`(min-width: ${RESPONSIVE_DRAWER.WIDE_BP}px)`);

  if (isWide) return leftDrawerWidth;
  // 60 px = sidebar; NARROW_PX = chat info drawer width when open.
  return `min(calc(100vw - 60px - ${RESPONSIVE_DRAWER.NARROW_PX}px), ${LEFT_DRAWER_MAX_NARROW_PX}px)`;
};

const SIDEBAR_WIDTH_PX = 60;

export const CustomDrawer: React.FC<CustomDrawerProps> = ({
  open,
  children,
  fullWidth = false,
  testId = "right-drawer",
  anchor = "right",
}: CustomDrawerProps) => {
  const responsiveRightWidth = useRightDrawerWidth();
  const responsiveLeftWidth = useLeftDrawerWidth();
  const isNarrow = useMediaQuery(
    `(max-width: ${RESPONSIVE_DRAWER.WIDE_BP - 1}px)`,
  );
  let width: string = "0px";
  switch (anchor) {
    case "left":
      width = responsiveLeftWidth;
      break;
    case "right":
      width = responsiveRightWidth;
      break;
  }

  // At narrow widths the left-anchored drawer otherwise covers the 60 px
  // sidebar — push it right by the sidebar width (issue #1240).
  const shouldOffsetForSidebar = anchor === "left" && isNarrow;

  return (
    <Drawer
      sx={{
        backgroundColor: theme.palette.background.default,
        width: width,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          position: open ? "absolute" : "fixed",
          width: fullWidth ? "100%" : width,
          ...(shouldOffsetForSidebar && { left: `${SIDEBAR_WIDTH_PX}px` }),
          boxSizing: "border-box",
          overflow: "hidden",
          transition: {
            xl: "none !important",
          },
        },
      }}
      variant="persistent"
      anchor={anchor}
      open={open}
      data-testid={testId}
    >
      {open && children}
    </Drawer>
  );
};

export default CustomDrawer;
